/**
 * The Cessation section's own vocabulary, mapped onto the panel-wide design
 * tokens in component/common/designSystem/theme.js — the same split
 * scanTheme.js and selfDevelopmentTheme.js use: only domain meaning lives here,
 * palettes belong to the design system.
 */
import { CHART_COLORS } from '../../../../component/common/designSystem';

/** The two programmes, in the order the section presents them everywhere. */
export const PROGRAMME_ORDER = ['puff', 'drink'];

export const PROGRAMME_LABELS = {
  puff: 'Last Puff',
  drink: 'Last Drink'
};

export const PROGRAMME_PATHS = {
  puff: 'last-puff',
  drink: 'last-drink'
};

/** Path segment back to the key the backend expects. */
export const PROGRAMME_BY_PATH = {
  'last-puff': 'puff',
  'last-drink': 'drink'
};

/**
 * One hue per programme, fixed, so a programme keeps its colour on the overview
 * donut, in the comparison table and on its own page. Neither hue carries a
 * verdict — quitting smoking and quitting drinking are two different things to
 * be doing, not two points on a scale.
 */
export const PROGRAMME_COLORS = {
  puff: CHART_COLORS.orange,
  drink: CHART_COLORS.blue
};

export const programmeColor = (key) => PROGRAMME_COLORS[key] || CHART_COLORS.gray;

export const PROGRAMME_RAMP = PROGRAMME_ORDER.map((key) => PROGRAMME_COLORS[key]);

/**
 * Last Puff only / Both / Last Drink only. The middle slice is the one that
 * matters, so it gets the strongest colour: somebody stopping smoking and
 * drinking at once is doing something considerably harder than either alone.
 */
export const OVERLAP_RAMP = [CHART_COLORS.orange, CHART_COLORS.moss, CHART_COLORS.blue];

/**
 * The current run without a slip: "slipped today" through "over a year".
 *
 * This is the one ramp in the section that reads as a scale, and it climbs to
 * green. Red at the left describes a hard day, not a failure — which is why the
 * copy beside it says "slipped today" and never anything stronger.
 */
export const RUN_RAMP = [
  CHART_COLORS.red,
  CHART_COLORS.orange,
  CHART_COLORS.amber,
  CHART_COLORS.teal,
  CHART_COLORS.green,
  CHART_COLORS.greenDeep
];

/** How long somebody has been tracking, regardless of how it is going. */
export const TENURE_RAMP = [
  CHART_COLORS.brandSoft,
  CHART_COLORS.blue,
  CHART_COLORS.brand,
  CHART_COLORS.moss,
  CHART_COLORS.greenDeep
];

/**
 * Craving strength, none through overwhelming. Runs green → red because a
 * stronger craving is a harder day — it is a reading on the difficulty of the
 * task, not on the person.
 */
export const INTENSITY_RAMP = [
  CHART_COLORS.green,
  CHART_COLORS.teal,
  CHART_COLORS.amber,
  CHART_COLORS.orange,
  CHART_COLORS.red
];

/** Distinct days logged in the last thirty — sparse to daily. */
export const ACTIVITY_RAMP = [
  CHART_COLORS.gray,
  CHART_COLORS.brandSoft,
  CHART_COLORS.blue,
  CHART_COLORS.brand,
  CHART_COLORS.greenDeep
];

/** Ranked lists — triggers, motivations, reasons. No verdict, just order. */
export const RANK_RAMP = [
  CHART_COLORS.brand,
  CHART_COLORS.blue,
  CHART_COLORS.teal,
  CHART_COLORS.greenDeep,
  CHART_COLORS.amber,
  CHART_COLORS.orange
];

/** How many previous attempts somebody has made. First attempt first. */
export const ATTEMPT_RAMP = [
  CHART_COLORS.green,
  CHART_COLORS.teal,
  CHART_COLORS.blue,
  CHART_COLORS.amber,
  CHART_COLORS.orange
];
